import { luminance, parseHex } from "./color";

/**
 * Spines are drawn in CSS rather than fetched: no catalogue has spine artwork
 * for most English-language books, and Kindle editions have no spine at all.
 * Colours come from the book's cover when it has been sampled; everything else
 * is derived from the book's id, so a spine looks the same on every render.
 */

type SpineInput = {
  id: string;
  pages: number | null;
  spineColor: string | null;
  accentColor: string | null;
};

export type Spine = {
  width: number;
  height: number;
  /** Width of the front cover, at the usual 2:3 trim. */
  coverWidth: number;
  background: string;
  foreground: string;
  accent: string;
  accentForeground: string;
  trim: "plain" | "bands" | "head" | "foot";
  capitals: boolean;
  /** Picks the spine's typeface from the caller's list, modulo its length. */
  typeface: number;
  /**
   * The catalogue does not record the edition, so this is chosen at random
   * per book, the same way every time.
   */
  binding: "hardback" | "paperback";
};

// Cloth tones for books whose cover has not been sampled, each with a foil.
const BINDINGS = [
  { spine: "#7a2e2a", accent: "#d9b46a" },
  { spine: "#1f3a5f", accent: "#c9d3df" },
  { spine: "#2f4f3a", accent: "#d8c48e" },
  { spine: "#4b2e4f", accent: "#e0c9a6" },
  { spine: "#8a5a2b", accent: "#f1e2c4" },
  { spine: "#36454f", accent: "#e3b04b" },
  { spine: "#e6dcc6", accent: "#8c3b2f" },
  { spine: "#1d5c5c", accent: "#f0d9a8" },
  { spine: "#a3472b", accent: "#f4e6d0" },
  { spine: "#2b2b2e", accent: "#c9a45c" },
];

const TRIMS: Spine["trim"][] = ["plain", "bands", "head", "foot"];

export const SPINE_MAX_HEIGHT = 196;
const SPINE_MIN_HEIGHT = 160;

export function spineFor(book: SpineInput): Spine {
  const hash = fnv1a(book.id);
  const binding = BINDINGS[hash % BINDINGS.length] ?? { spine: "#36454f", accent: "#e3b04b" };
  const background = book.spineColor ?? binding.spine;
  const accent = book.accentColor ?? binding.accent;
  const height = spineHeight(hash);

  return {
    width: spineWidth(book.pages),
    height,
    coverWidth: Math.round(height * 0.66),
    background,
    foreground: inkFor(background),
    accent,
    accentForeground: inkFor(accent),
    trim: TRIMS[(hash >>> 16) % TRIMS.length] ?? "plain",
    capitals: ((hash >>> 20) & 3) === 0,
    typeface: hash >>> 24,
    binding: (hash >>> 4) & 1 ? "hardback" : "paperback",
  };
}

/** Near-black or near-white, whichever reads better on the given colour. */
/**
 * Width over height of a book's spine on the shelf. Generated spine art is
 * drawn at these proportions so the texture is not stretched onto the book.
 */
export function spineAspect(book: Pick<SpineInput, "id" | "pages">): number {
  return spineWidth(book.pages) / spineHeight(fnv1a(book.id));
}

function spineWidth(pages: number | null): number {
  return Math.round(Math.min(60, Math.max(22, 14 + (pages ?? 320) / 24)));
}

function spineHeight(hash: number): number {
  return SPINE_MAX_HEIGHT - ((hash >>> 8) % (SPINE_MAX_HEIGHT - SPINE_MIN_HEIGHT));
}

export function inkFor(background: string): string {
  const rgb = parseHex(background);
  return rgb && luminance(rgb) > 0.4 ? "#1c1917" : "#faf7f2";
}

type ShelvedBook = {
  shelf: "reading" | "read" | "to-read";
  finishedOn: string | null;
  addedAt: Date;
};

export type ShelfSection<T> = { key: string; label: string; books: T[] };

/**
 * Orders the public bookshelf: what is being read now, then finished books by
 * year with the most recent first. Want-to-read stays off the public page.
 */
export function groupShelves<T extends ShelvedBook>(books: T[]): ShelfSection<T>[] {
  const byAdded = (a: T, b: T) => b.addedAt.getTime() - a.addedAt.getTime();
  const reading = books.filter((b) => b.shelf === "reading").sort(byAdded);
  const read = books.filter((b) => b.shelf === "read");

  const years = new Map<string, T[]>();
  const undated: T[] = [];
  for (const b of read) {
    if (!b.finishedOn) {
      undated.push(b);
      continue;
    }
    const year = b.finishedOn.slice(0, 4);
    years.set(year, [...(years.get(year) ?? []), b]);
  }

  const sections: ShelfSection<T>[] = [];
  if (reading.length > 0) {
    sections.push({ key: "reading", label: "Reading", books: reading });
  }
  for (const year of [...years.keys()].sort().reverse()) {
    const finished = (years.get(year) ?? []).sort((a, b) =>
      (b.finishedOn ?? "").localeCompare(a.finishedOn ?? ""),
    );
    sections.push({ key: year, label: year, books: finished });
  }
  if (undated.length > 0) {
    sections.push({ key: "earlier", label: "Earlier", books: undated.sort(byAdded) });
  }
  return sections;
}

/**
 * Splits books, in order, into piles of about the same height, none taller
 * than `maxHeight` unless one book is on its own.
 */
export function pileUp<T>(books: T[], thickness: (book: T) => number, maxHeight: number): T[][] {
  const total = books.reduce((sum, book) => sum + thickness(book), 0);
  const count = Math.ceil(total / maxHeight);
  const target = total / count;
  const piles: T[][] = [];
  let height = 0;
  for (const book of books) {
    const t = thickness(book);
    const pile = piles.at(-1);
    // A new pile once this one is full, or once it is past its share and more are due.
    if (!pile || height + t > maxHeight || (height + t / 2 > target && piles.length < count)) {
      piles.push([book]);
      height = t;
    } else {
      pile.push(book);
      height += t;
    }
  }
  return piles;
}

/**
 * Splits `items` into rows of at most `perRow`, as even as possible, with any
 * short row first. Piles on a desk are laid out this way: the front row, which
 * the desk is drawn against, is always full.
 */
export function evenRows<T>(items: T[], perRow: number): T[][] {
  if (items.length === 0) return [];
  const count = Math.ceil(items.length / Math.max(1, perRow));
  const share = Math.ceil(items.length / count);
  const rows: T[][] = [];
  for (let end = items.length; end > 0; end -= share) rows.push(items.slice(Math.max(0, end - share), end));
  return rows.reverse();
}

type FacedInput = {
  id: string;
  shelf: "reading" | "read" | "to-read";
  rating: number | null;
  coverUrl: string | null;
};

/**
 * Orders the books for a shelf that shows some of them cover out, the way a
 * bookseller would: what is being read first, then five-star books, spaced
 * `cadence` slots apart starting at slot `lead`. The rest keep their order.
 */
export function faceOut<T extends FacedInput>(books: T[], lead = 5, cadence = 14): { book: T; faced: boolean }[] {
  const slots = Math.ceil(books.length / cadence);
  const reading = books.filter((b) => b.shelf === "reading");
  const favourites = books.filter((b) => b.shelf !== "reading" && b.rating === 5 && b.coverUrl);
  const chosen = new Set([...reading, ...favourites].slice(0, Math.max(slots, reading.length)));
  const queue = books.filter((b) => chosen.has(b));
  const rest = books.filter((b) => !chosen.has(b));
  const placed: { book: T; faced: boolean }[] = [];
  while (queue.length > 0 || rest.length > 0) {
    const due = placed.length >= lead && (placed.length - lead) % cadence === 0;
    const next = queue.length > 0 && (due || rest.length === 0) ? queue.shift() : rest.shift();
    if (next) placed.push({ book: next, faced: chosen.has(next) });
  }
  return placed;
}

/**
 * Where each shelf starts when items of the given widths flow along shelves
 * `capacity` wide, as indexes into `widths`. The last shelf keeps `reserve`
 * free for whatever stands beside the books.
 */
export function packShelves(widths: number[], capacity: number, reserve = 0): number[] {
  const starts: number[] = [];
  let used = Infinity;
  widths.forEach((width, index) => {
    if (used + width > capacity) {
      starts.push(index);
      used = 0;
    }
    used += width;
  });
  if (used + reserve > capacity && widths.length > 1) {
    // Start another shelf, taking just enough books that the reserve fits beside them.
    let index = widths.length - 1;
    let tail = widths[index] ?? 0;
    while (index > (starts.at(-1) ?? 0) + 1 && tail + reserve < capacity * 0.25) {
      index -= 1;
      tail += widths[index] ?? 0;
    }
    if (index > (starts.at(-1) ?? 0)) starts.push(index);
  }
  return starts;
}

/** The narrowest shelf that holds the items on `rows` shelves, for a case that scrolls sideways. */
export function balancedCapacity(widths: number[], rows: number, reserve = 0): number {
  const total = widths.reduce((sum, width) => sum + width, 0) + reserve;
  let capacity = Math.max(Math.ceil(total / Math.max(1, rows)), ...widths, reserve);
  while (packShelves(widths, capacity, reserve).length > rows) capacity += 8;
  return capacity;
}

type WornBook = {
  id: string;
  shelf: "reading" | "read" | "to-read";
  finishedOn: string | null;
  pages: number | null;
  rating: number | null;
};

const YEAR = 365.25 * 24 * 60 * 60 * 1000;

/**
 * How worn a book looks, from 0 for one just bought to 1 for one read to
 * pieces. Finished books wear with the years since; long books and loved
 * ones a little more; and each book a little differently from the next.
 */
export function wearFor(book: WornBook, now = new Date()): number {
  const own = ((fnv1a(book.id) >>> 12) % 1000) / 1000 - 0.5;
  let wear: number;
  if (book.shelf === "to-read") wear = 0.05;
  else if (book.shelf === "reading") wear = 0.3;
  else if (book.finishedOn) {
    const years = Math.max(0, now.getTime() - Date.parse(book.finishedOn)) / YEAR;
    wear = 0.3 + Math.min(0.5, years * 0.12);
  } else wear = 0.75;
  if ((book.pages ?? 0) > 500) wear += 0.08;
  if ((book.rating ?? 0) >= 5) wear += 0.08;
  return Math.min(1, Math.max(0.02, wear + own * 0.2));
}

function fnv1a(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i++) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}
